import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Layers, Shield, UserPlus, Users } from 'lucide-react';
import type { OrgMemberSummary } from '../../generated/api/types.gen.js';
import {
  getOrganizationOptions,
  listOrgMembersOptions,
  listTeamsOptions,
} from '../../generated/api/@tanstack/react-query.gen.js';
import { useAuthStore } from '../../store/index.js';
import {
  Avatar,
  Badge,
  Button,
  Callout,
  Card,
  DataTable,
  EmptyState,
  SkeletonList,
} from '../../components/ui/index.js';
import type { DataTableColumn } from '../../components/ui/index.js';
import { DashboardGrid, DashboardPage } from '../../components/page/DashboardPage.js';
import { buttonStyles } from '../../components/tailgrids/core/button.js';
import { Can } from '../../components/auth/Can.js';
import { PAGE_HELP } from '../../help/index.js';
import { orgPlanBadgeVariant } from './org-badges.js';
import { formatDate } from './user-labels.js';

const sentenceCase = (value: string) => value.charAt(0).toUpperCase() + value.slice(1);

function memberName(member: OrgMemberSummary): string {
  return member.fullName || member.username || member.email;
}

const MEMBER_COLUMNS: DataTableColumn<OrgMemberSummary>[] = [
  {
    key: 'member',
    header: 'Member',
    sortable: true,
    sortValue: (member) => memberName(member).toLowerCase(),
    render: (member) => (
      <div className="flex min-w-0 items-center gap-3">
        <Avatar kind="human" label={memberName(member)} size="sm" />
        <div className="min-w-0">
          <span className="block truncate text-sm font-medium text-heading">{memberName(member)}</span>
          <span className="block truncate text-xs text-muted">{member.email}</span>
        </div>
      </div>
    ),
  },
  {
    key: 'status',
    header: 'Status',
    render: (member) => (
      <Badge
        variant={member.status === 'approved' ? 'success' : member.status === 'pending' ? 'warning' : 'neutral'}
        dot
      >
        {sentenceCase(member.status)}
      </Badge>
    ),
  },
  {
    key: 'joined',
    header: 'Joined',
    sortable: true,
    sortValue: (member) => member.createdAt,
    render: (member) => <span className="text-sm text-muted">{formatDate(member.createdAt)}</span>,
  },
];

export const OrgSettingsPage: React.FC = () => {
  const orgId = useAuthStore((s) => s.user?.organizationId ?? null);
  const path = { id: orgId ?? 0 };
  const enabled = orgId !== null;

  const orgQuery = useQuery({ ...getOrganizationOptions({ path }), enabled });
  const membersQuery = useQuery({ ...listOrgMembersOptions({ path }), enabled });
  const teamsQuery = useQuery({ ...listTeamsOptions({ path: { id: String(path.id) } }), enabled });

  const org = orgQuery.data?.organization;
  const members = membersQuery.data?.members ?? [];
  const teams = teamsQuery.data?.teams ?? [];

  const failed = orgQuery.isError || membersQuery.isError || teamsQuery.isError;
  const retry = () => {
    void orgQuery.refetch();
    void membersQuery.refetch();
    void teamsQuery.refetch();
  };

  if (!enabled) {
    return (
      <DashboardPage title="Organization" helpContent={PAGE_HELP.organizations}>
        <EmptyState
          icon={Shield}
          title="No organization"
          description="Your account is not part of an organization yet. Ask an administrator to add you."
        />
      </DashboardPage>
    );
  }

  return (
    <DashboardPage
      title={org?.name ?? 'Organization'}
      subtitle={org ? `Members, teams and plan for ${org.slug}` : 'Members, teams and plan'}
      helpContent={PAGE_HELP.organizations}
      actions={
        <Can policy="CanInviteUsers">
          <Link
            to="/organization/users"
            className={buttonStyles({ variant: 'primary', appearance: 'fill', size: 'sm' })}
          >
            <UserPlus size={14} aria-hidden="true" />
            Invite people
          </Link>
        </Can>
      }
      stats={[
        { label: 'Members', value: membersQuery.isPending ? '…' : members.length, icon: Users },
        { label: 'Teams', value: teamsQuery.isPending ? '…' : teams.length, icon: Layers },
        {
          label: 'Plan',
          value: org ? (
            <Badge variant={orgPlanBadgeVariant(org.plan)} size="md">
              {sentenceCase(org.plan)}
            </Badge>
          ) : (
            '…'
          ),
          icon: Shield,
        },
      ]}
      alert={
        failed ? (
          <Callout
            tone="danger"
            icon={AlertTriangle}
            title="Some organization details did not load"
            actions={
              <Button size="sm" variant="secondary" onClick={retry}>
                Retry
              </Button>
            }
          >
            What you see below may be incomplete.
          </Callout>
        ) : undefined
      }
    >
      <DashboardGrid>
        <div className="xl:col-span-8">
          <DataTable
            framed
            columns={MEMBER_COLUMNS}
            rows={members}
            rowKey={(member) => member.id}
            loading={membersQuery.isPending}
            pageSize={10}
            aria-label="Members"
            empty={
              <EmptyState
                icon={Users}
                title="No members yet"
                description="People who accept an invitation to this organization appear here."
              />
            }
          />
        </div>
        <div className="xl:col-span-4">
          <Card title="Teams">
            {teamsQuery.isPending ? (
              <SkeletonList count={3} />
            ) : teams.length === 0 ? (
              <p className="text-sm text-muted">No teams yet.</p>
            ) : (
              <ul className="space-y-3">
                {teams.map((team) => (
                  <li key={team.id} className="flex items-start gap-2">
                    <Layers size={16} className="mt-0.5 shrink-0 text-accent-text" aria-hidden="true" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-heading">{team.name}</p>
                      {team.brief && <p className="text-xs text-muted">{team.brief}</p>}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>
      </DashboardGrid>
    </DashboardPage>
  );
};
